import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';

import { DateCell } from '../../components/DateCell/DateCell';
import { ReportsKindCell } from '../../components/ReportsKindCell/ReportsKindCell';
import { ReportsStatusCell } from '../../components/ReportsStatusCell/ReportsStatusCell';
import { StyledLink } from '../../components/StyledLink/StyledLink';
import { TextCell } from '../../components/TextCell/TextCell';
import { filterArrayByNullAndUndefined } from '../../mappers/common/common';
import { addSettingsToDecl, arrangeInOrder } from '../../service/DeclarationHelper';
import { ColumnsSettings, ColumnsSettingsViewDict, reportsClient } from '../../typings/common';
import { GridRow } from '../../typings/domain';
import { GridCellProps, GridColumnDecl, GridColumnStaticDecl } from '../../typings/grid';
import { ReportStatus } from '../../typings/reports';
import { ReportClient } from '../../typings/reportsClient';

import * as keyset from './reportsClient.i18n';

const i18n = i18nFactory(keyset);

export enum ReportClientColumnKey {
    id = 'id',
    agencyLogin = 'agencyLogin',
    reportKind = 'reportKind',
    buildDate = 'buildDate',
    startDate = 'startDate',
    endDate = 'endDate',
    status = 'status',
    link = 'link'
}

export const reportsClientColumnsOrder = [
    'id',
    'agencyLogin',
    'reportKind',
    'buildDate',
    'startDate',
    'endDate',
    'status',
    'link'
];

export function getDynamicReportsClientColumnsFields(): ColumnsSettings {
    return {
        columnOrder: reportsClientColumnsOrder,
        columnViewInfo: {
            id: {
                width: 90,
                widthCompact: 90,
                hidden: false
            },
            agencyLogin: {
                width: 190,
                widthCompact: 190,
                hidden: false
            },
            reportKind: {
                width: 120,
                widthCompact: 120,
                hidden: false
            },
            buildDate: {
                width: 200,
                widthCompact: 200,
                hidden: false
            },
            startDate: {
                width: 150,
                widthCompact: 150,
                hidden: false
            },
            endDate: {
                width: 150,
                widthCompact: 150,
                hidden: false
            },
            status: {
                width: 120,
                widthCompact: 120,
                hidden: false
            },
            link: {
                width: 150,
                widthCompact: 150,
                hidden: false
            }
        }
    };
}

function getFixedReportsColumns(): Array<GridColumnStaticDecl<ReportClient>> {
    return [
        {
            title: i18n('id'),
            key: ReportClientColumnKey.id,
            isSortable: false,
            fixed: true,
            CellComponent: React.memo(
                ({ rowData: { id } }: GridCellProps<ReportClient>): JSX.Element => <TextCell>{id}</TextCell>
            )
        }
    ];
}

function getReportsColumnDecl(
    showAdditionalColumns: boolean,
    login?: string
): Array<GridColumnStaticDecl<ReportClient>> {
    return filterArrayByNullAndUndefined<GridColumnStaticDecl<ReportClient>>([
        showAdditionalColumns
            ? {
                  title: i18n('agency-login'),
                  key: ReportClientColumnKey.agencyLogin,
                  isSortable: false,
                  fixed: false,
                  minWidth: 150,
                  CellComponent: React.memo(
                      ({ rowData: { agencyLogin } }: GridCellProps<ReportClient>): JSX.Element => (
                          <TextCell>{agencyLogin}</TextCell>
                      )
                  )
              }
            : null,
        showAdditionalColumns
            ? {
                  title: i18n('report-kind'),
                  key: ReportClientColumnKey.reportKind,
                  isSortable: false,
                  fixed: false,
                  minWidth: 100,
                  CellComponent: React.memo(
                      ({ rowData: { reportKind } }: GridCellProps<ReportClient>): JSX.Element => (
                          <ReportsKindCell kind={reportKind} />
                      )
                  )
              }
            : null,
        {
            title: i18n('build-date'),
            key: ReportClientColumnKey.buildDate,
            isSortable: false,
            fixed: false,
            CellComponent: React.memo(
                ({ rowData: { buildDate } }: GridCellProps<ReportClient>): JSX.Element => <DateCell date={buildDate} />
            )
        },
        {
            title: i18n('start-date'),
            key: ReportClientColumnKey.startDate,
            isSortable: false,
            fixed: false,
            CellComponent: React.memo(
                ({ rowData: { dateStart } }: GridCellProps<ReportClient>): JSX.Element => (
                    <DateCell date={dateStart} showTime={false} />
                )
            )
        },
        {
            title: i18n('end-date'),
            key: ReportClientColumnKey.endDate,
            isSortable: false,
            fixed: false,
            CellComponent: React.memo(
                ({ rowData: { dateEnd } }: GridCellProps<ReportClient>): JSX.Element => (
                    <DateCell date={dateEnd} showTime={false} />
                )
            )
        },
        {
            title: i18n('status'),
            key: ReportClientColumnKey.status,
            isSortable: false,
            fixed: false,
            CellComponent: React.memo(
                ({ rowData: { status } }: GridCellProps<ReportClient>): JSX.Element => (
                    <ReportsStatusCell status={status} />
                )
            )
        },
        {
            title: i18n('link'),
            key: ReportClientColumnKey.link,
            isSortable: false,
            fixed: false,
            CellComponent: React.memo(
                ({ rowData: { status, id } }: GridCellProps<ReportClient>): JSX.Element => {
                    const href = login
                        ? `/web-api/agency_offline_report/download?report_id=${id}&ulogin=${login}`
                        : `/web-api/agency_offline_report/download?report_id=${id}`;
                    return (
                        <TextCell>
                            <StyledLink href={href} disabled={status !== ReportStatus.ready} title={i18n('download')}>
                                {i18n('download')}
                            </StyledLink>
                        </TextCell>
                    );
                }
            )
        }
    ]);
}

export function getReportsClientColumns(
    columnsSettings: ColumnsSettingsViewDict,
    showAdditionalColumns: boolean,
    login?: string
): Array<GridColumnDecl<GridRow>> {
    const fixedColumns = getFixedReportsColumns().map(column => {
        return addSettingsToDecl(column, columnsSettings, reportsClient);
    });

    const result = getReportsColumnDecl(showAdditionalColumns, login).map(column => {
        return addSettingsToDecl(column, columnsSettings, reportsClient);
    });
    return fixedColumns.concat(arrangeInOrder(result, columnsSettings[reportsClient].columnOrder));
}
